/**
 * The two session colour schemes the product can run.
 *
 * `LEGACY_V1` is the scheme the calendar has always had: fifteen colours, made by crossing the state
 * of a session with the kind of appointment. `SIMPLIFIED_V2` reduces that to colours that describe
 * the state alone — the kind of appointment is already shown by the person, video and phone icons on
 * each session.
 *
 * Both exist at the same time. Which one a person is on is decided by the server (from the tenant's
 * rollout stage and the person's roles) and handed to the browser; see `appStore.sessionColourScheme`.
 *
 * A scheme is the single source for the colours, the calendar key, the names used in the availability
 * filter, the wording of the session tooltip, and what a session title is made of — so those can
 * never drift apart from one another.
 *
 * This module is deliberately free of imports: it is pure data and pure functions, which is what lets
 * every rule in it be unit tested on its own.
 */

export const SESSION_COLOUR_SCHEME = {
  LEGACY_V1: 'LegacyV1',
  SIMPLIFIED_V2: 'SimplifiedV2',
} as const

/**
 * The tenant setting that drives the rollout, and the three positions it can be in. The values must
 * match `SessionColourSchemeStage` on the server exactly — it is the server that reads this setting
 * and decides who gets which scheme.
 */
export const SESSION_COLOUR_SCHEME_SETTING_KEY =
  'ICS.Calendar.SessionColourScheme'

export const SESSION_COLOUR_SCHEME_STAGE = {
  LEGACY: 'Legacy',
  STAFF_ONLY: 'StaffOnly',
  ALL: 'All',
} as const

/** Wording for the rollout setting on the Company Setting screen. */
export const SESSION_COLOUR_SCHEME_STAGE_OPTIONS = [
  {
    id: SESSION_COLOUR_SCHEME_STAGE.LEGACY,
    displayName: 'Extended Legend Scheme - Everyone',
  },
  {
    id: SESSION_COLOUR_SCHEME_STAGE.STAFF_ONLY,
    displayName: 'Reduced Legend Scheme - Internal Staff Only',
  },
  {
    id: SESSION_COLOUR_SCHEME_STAGE.ALL,
    displayName: 'Reduced Legend Scheme - Everyone',
  },
]

/**
 * The key the server sends the already-resolved scheme under, in the per-user settings payload. The
 * browser never works the scheme out for itself: the list of staff roles lives in the server code, and
 * a second copy here would be one more thing that can drift.
 */
export const RESOLVED_SESSION_COLOUR_SCHEME_KEY =
  'ICS.Calendar.SessionColourScheme.Resolved'

export type SessionColourSchemeId =
  (typeof SESSION_COLOUR_SCHEME)[keyof typeof SESSION_COLOUR_SCHEME]

/**
 * Ids of the availability filter values. These are the values of `SessionBookingStatusEnum` in
 * `constants.ts`, repeated here rather than imported so this module stays import-free — and pinned by
 * a unit test so the two cannot drift. They are part of the calendar's page address, so they must not
 * change: a link someone bookmarked before this feature has to keep opening the same calendar.
 */
export type SessionBookingStatusId = '2' | '3' | '4' | '5' | '6'

const BOOKING_STATUS = {
  UNAVAILABLE: '2' as SessionBookingStatusId,
  UNBOOKED_CONFIRMED_AVAILABLE: '3' as SessionBookingStatusId,
  UNBOOKED_UNCONFIRMED_AVAILABLE: '4' as SessionBookingStatusId,
  BOOKED_CONFIRMED: '5' as SessionBookingStatusId,
  BOOKED_UNCONFIRMED: '6' as SessionBookingStatusId,
}

/** Session status as it arrives from the server. */
const STATUS = {
  AVAILABLE: 'Available',
  PENDING: 'Pending',
  UNAVAILABLE: 'Unavailable',
}

/** Kind of appointment as it arrives from the server. */
const ACCEPT = {
  INPERSON: 'INPERSON',
  VIDEO: 'VIDEO',
  BOTH: 'BOTH',
  NDIS: 'NDIS',
  OT: 'OT',
  OT_VIDEO: 'OT_VIDEO',
  CO_TELECONFERENCE: 'CO_TELECONFERENCE',
}

const WHITE = '#ffffff'
const BLACK = '#000000'

/** What the calendar needs to know about a session in order to colour it. */
export interface SessionColourContext {
  booked?: boolean
  confirmed?: boolean
  isReserved?: boolean
  /**
   * Whether the session is on hold *and* the hold has not run out. Worked out by the caller against
   * its own clock, so choosing a colour stays a pure decision.
   */
  isOnHoldNow?: boolean
  sessionStatus?: string
  sessionAcceptType?: string
  /**
   * Whether this viewer is shown reservations at all. Staff and case managers are; specialists are
   * not, and see the underlying availability colour instead. Unchanged from today.
   */
  canSeeReserved?: boolean
}

export interface SessionColourResult {
  /** Stable name of the state this colour stands for, e.g. `Available`. */
  key: string
  backgroundColor: string
  borderColor: string
  textColor: string
}

/** One swatch of the calendar key. */
export interface SessionLegendEntry {
  key: string
  label: string
  backgroundColor: string
  textColor: string
  /** Only shown when the tenant has this feature switched on. */
  requiresFeature?: 'Assessment' | 'OT' | 'NDIS'
  /** Draw a separator before this swatch. Used by the legacy key, which has always had two. */
  dividerBefore?: boolean
  /**
   * Width class for the swatch, where the legacy key pins one. Layout detail rather than data, but
   * it lives here so the legacy key keeps the exact spacing it has today.
   */
  widthClass?: string
  marginLeft?: string
}

export interface SessionLegendRow {
  /** Caption printed before the swatches. The simplified key needs none. */
  caption?: 'Unbooked (CA)' | 'Unbooked (UA)'
  entries: SessionLegendEntry[]
}

export interface SessionStatusLabel {
  text: string
  /** Whether the kind of appointment is appended, as in `Available - In-person`. */
  withAcceptType: boolean
}

export interface SessionColourScheme {
  id: SessionColourSchemeId
  resolve(context: SessionColourContext): SessionColourResult
  legend: SessionLegendRow[]
  /** Swatches to leave out of the key for a case manager. */
  hiddenLegendKeysForCaseManager: string[]
  /** Availability filter values to leave out for a case manager. */
  hiddenFilterIdsForCaseManager: SessionBookingStatusId[]
  filterTitle: string
  filterOptions: Array<{ id: SessionBookingStatusId; displayName: string }>
  /** What a session title is built from, in order. */
  titleParts: Array<'specialist' | 'client' | 'centre'>
  statusLabel(context: SessionColourContext): SessionStatusLabel
}

/* ------------------------------------------------------------------ legacy */

const LEGACY_COLOURS = {
  ConfirmedInPerson: '#009b72',
  ConfirmedTelehealth: '#683bb1',
  ConfirmedInPersonOrTelehealth: '#febf40',

  UnconfirmedInPerson: '#99d7c6',
  UnconfirmedTelehealth: '#c09dfa',
  UnconfirmedInPersonOrTelehealth: '#fedc96',

  BookedConfirmed: '#a62639',
  BookedUnconfirmed: '#ff7373',

  Unavailable: '#c1c1c1',
  Reserved: '#1fc0da',

  ConfirmedOT: '#006eff',
  UnconfirmedOT: '#559eff',
  ConfirmedNDIS: '#6600cc',
  UnconfirmedNDIS: '#b366ff',
  OnHold: '#c27400',
}

/**
 * A faithful transcription of the colour rules the calendar has always run, quirks included: a booked
 * session keeps the starting green border, and an unavailable session has its background replaced but
 * not its border. They are kept so a tenant still on this scheme sees no change whatsoever — the
 * simplified scheme is where the borders are put right.
 */
function resolveLegacy(item: SessionColourContext): SessionColourResult {
  let key = 'Available'
  let textColor = WHITE
  let backgroundColor = '#009B72'
  let borderColor = '#009B72'

  if (!item.booked) {
    if (item.isOnHoldNow) {
      return {
        key: 'OnHold',
        backgroundColor: LEGACY_COLOURS.OnHold,
        borderColor: LEGACY_COLOURS.OnHold,
        textColor,
      }
    }

    if (item.isReserved && item.canSeeReserved) {
      key = 'Reserved'
      backgroundColor = LEGACY_COLOURS.Reserved
      borderColor = LEGACY_COLOURS.Reserved
    } else if (item.sessionStatus === STATUS.AVAILABLE) {
      key = 'ConfirmedInPerson'
      backgroundColor = LEGACY_COLOURS.ConfirmedInPerson

      if (item.sessionAcceptType === ACCEPT.INPERSON) {
        backgroundColor = LEGACY_COLOURS.ConfirmedInPerson
        borderColor = LEGACY_COLOURS.ConfirmedInPerson
      } else if (
        item.sessionAcceptType === ACCEPT.VIDEO ||
        item.sessionAcceptType === ACCEPT.CO_TELECONFERENCE
      ) {
        key = 'ConfirmedTelehealth'
        backgroundColor = LEGACY_COLOURS.ConfirmedTelehealth
        borderColor = LEGACY_COLOURS.ConfirmedTelehealth
      } else if (item.sessionAcceptType === ACCEPT.BOTH) {
        key = 'ConfirmedInPersonOrTelehealth'
        backgroundColor = LEGACY_COLOURS.ConfirmedInPersonOrTelehealth
        borderColor = LEGACY_COLOURS.ConfirmedInPersonOrTelehealth
        textColor = BLACK
      } else if (
        item.sessionAcceptType === ACCEPT.OT ||
        item.sessionAcceptType === ACCEPT.OT_VIDEO
      ) {
        key = 'ConfirmedOT'
        backgroundColor = LEGACY_COLOURS.ConfirmedOT
        borderColor = LEGACY_COLOURS.ConfirmedOT
      } else if (item.sessionAcceptType === ACCEPT.NDIS) {
        key = 'ConfirmedNDIS'
        backgroundColor = LEGACY_COLOURS.ConfirmedNDIS
        borderColor = LEGACY_COLOURS.ConfirmedNDIS
      }
    } else {
      key = 'UnconfirmedInPerson'
      backgroundColor = LEGACY_COLOURS.UnconfirmedInPerson

      if (item.sessionAcceptType === ACCEPT.INPERSON) {
        backgroundColor = LEGACY_COLOURS.UnconfirmedInPerson
        borderColor = LEGACY_COLOURS.UnconfirmedInPerson
      } else if (
        item.sessionAcceptType === ACCEPT.VIDEO ||
        item.sessionAcceptType === ACCEPT.CO_TELECONFERENCE
      ) {
        key = 'UnconfirmedTelehealth'
        backgroundColor = LEGACY_COLOURS.UnconfirmedTelehealth
        borderColor = LEGACY_COLOURS.UnconfirmedTelehealth
      } else if (item.sessionAcceptType === ACCEPT.BOTH) {
        key = 'UnconfirmedInPersonOrTelehealth'
        backgroundColor = LEGACY_COLOURS.UnconfirmedInPersonOrTelehealth
        borderColor = LEGACY_COLOURS.UnconfirmedInPersonOrTelehealth
        textColor = BLACK
      } else if (
        item.sessionAcceptType === ACCEPT.OT ||
        item.sessionAcceptType === ACCEPT.OT_VIDEO
      ) {
        key = 'UnconfirmedOT'
        backgroundColor = LEGACY_COLOURS.UnconfirmedOT
        borderColor = LEGACY_COLOURS.UnconfirmedOT
      } else if (item.sessionAcceptType === ACCEPT.NDIS) {
        key = 'UnconfirmedNDIS'
        backgroundColor = LEGACY_COLOURS.UnconfirmedNDIS
        borderColor = LEGACY_COLOURS.UnconfirmedNDIS
      }
    }

    if (item.sessionStatus === STATUS.UNAVAILABLE) {
      key = 'Unavailable'
      backgroundColor = LEGACY_COLOURS.Unavailable
    }
  } else {
    key = item.confirmed ? 'BookedConfirmed' : 'BookedUnconfirmed'
    backgroundColor = item.confirmed
      ? LEGACY_COLOURS.BookedConfirmed
      : LEGACY_COLOURS.BookedUnconfirmed
    textColor = WHITE
  }

  return { key, backgroundColor, borderColor, textColor }
}

function legacyEntry(
  key: string,
  label: string,
  backgroundColor: string,
  requiresFeature?: SessionLegendEntry['requiresFeature'],
  textColor: string = WHITE,
  extra: Partial<SessionLegendEntry> = {}
): SessionLegendEntry {
  return { key, label, backgroundColor, textColor, requiresFeature, ...extra }
}

/** The legacy key sets these two apart with a separator and a fixed width. */
const LEGACY_BOOKED = { dividerBefore: true, widthClass: 'w-[200px]' }
const LEGACY_TRAILING = { dividerBefore: true, widthClass: 'w-[120px]' }

export const LEGACY_V1_SCHEME: SessionColourScheme = {
  id: SESSION_COLOUR_SCHEME.LEGACY_V1,
  resolve: resolveLegacy,
  legend: [
    {
      caption: 'Unbooked (CA)',
      entries: [
        legacyEntry(
          'ConfirmedInPerson',
          'In-person',
          LEGACY_COLOURS.ConfirmedInPerson,
          'Assessment'
        ),
        legacyEntry(
          'ConfirmedTelehealth',
          'Telehealth',
          LEGACY_COLOURS.ConfirmedTelehealth,
          'Assessment'
        ),
        legacyEntry(
          'ConfirmedInPersonOrTelehealth',
          'In-person/Telehealth',
          LEGACY_COLOURS.ConfirmedInPersonOrTelehealth,
          'Assessment',
          BLACK
        ),
        legacyEntry('ConfirmedOT', 'OT', LEGACY_COLOURS.ConfirmedOT, 'OT'),
        legacyEntry(
          'ConfirmedNDIS',
          'NDIS',
          LEGACY_COLOURS.ConfirmedNDIS,
          'NDIS'
        ),
        legacyEntry(
          'BookedConfirmed',
          'Booked (Confirmed)',
          LEGACY_COLOURS.BookedConfirmed
        ),
        legacyEntry(
          'Unavailable',
          'Unavailable',
          LEGACY_COLOURS.Unavailable,
          undefined,
          BLACK,
          { marginLeft: '17px' }
        ),
      ],
    },
    {
      caption: 'Unbooked (UA)',
      entries: [
        legacyEntry(
          'UnconfirmedInPerson',
          'In-person',
          LEGACY_COLOURS.UnconfirmedInPerson,
          'Assessment'
        ),
        legacyEntry(
          'UnconfirmedTelehealth',
          'Telehealth',
          LEGACY_COLOURS.UnconfirmedTelehealth,
          'Assessment'
        ),
        legacyEntry(
          'UnconfirmedInPersonOrTelehealth',
          'In-person/Telehealth',
          LEGACY_COLOURS.UnconfirmedInPersonOrTelehealth,
          'Assessment',
          BLACK
        ),
        legacyEntry('UnconfirmedOT', 'OT', LEGACY_COLOURS.UnconfirmedOT, 'OT'),
        legacyEntry(
          'UnconfirmedNDIS',
          'NDIS',
          LEGACY_COLOURS.UnconfirmedNDIS,
          'NDIS'
        ),
        legacyEntry(
          'BookedUnconfirmed',
          'Booked (Unconfirmed)',
          LEGACY_COLOURS.BookedUnconfirmed
        ),
        legacyEntry(
          'Reserved',
          'Reserved',
          LEGACY_COLOURS.Reserved,
          undefined,
          WHITE
        ),
      ],
    },
  ],
  /** A case manager is never sent an unavailable session, so the grey swatch only puzzles them. */
  hiddenLegendKeysForCaseManager: ['Unavailable'],
  /** Same reason as the swatch: filtering for unavailable sessions can return nothing for them. */
  hiddenFilterIdsForCaseManager: [BOOKING_STATUS.UNAVAILABLE],
  filterTitle: 'Session Booking Status',
  filterOptions: [
    { id: BOOKING_STATUS.UNAVAILABLE, displayName: 'Unavailable' },
    {
      id: BOOKING_STATUS.UNBOOKED_CONFIRMED_AVAILABLE,
      displayName: 'Unbooked (CA)',
    },
    {
      id: BOOKING_STATUS.UNBOOKED_UNCONFIRMED_AVAILABLE,
      displayName: 'Unbooked (UA)',
    },
    { id: BOOKING_STATUS.BOOKED_CONFIRMED, displayName: 'Booked (Confirmed)' },
    {
      id: BOOKING_STATUS.BOOKED_UNCONFIRMED,
      displayName: 'Booked (Unconfirmed)',
    },
  ],
  titleParts: ['specialist', 'client', 'centre'],
  statusLabel: item => {
    if (item.booked) {
      return {
        text: item.confirmed ? 'Booked (Confirmed)' : 'Booked (Unconfirmed)',
        withAcceptType: true,
      }
    }
    if (item.sessionStatus === STATUS.UNAVAILABLE) {
      return { text: 'Specialist Unavailable', withAcceptType: false }
    }
    return {
      text:
        item.sessionStatus === STATUS.PENDING
          ? 'Unbooked (UA)'
          : 'Unbooked (CA)',
      withAcceptType: true,
    }
  },
}

/* ------------------------------------------------------------- simplified */

const V2_TOKENS = {
  OnHold: {
    key: 'OnHold',
    label: 'On-Hold',
    backgroundColor: '#c27400',
    textColor: WHITE,
  },
  Unavailable: {
    key: 'Unavailable',
    label: 'Unavailable',
    backgroundColor: '#c1c1c1',
    textColor: BLACK,
  },
  Reserved: {
    key: 'Reserved',
    label: 'Reserved',
    backgroundColor: '#1fc0da',
    textColor: WHITE,
  },
  Confirmed: {
    key: 'Confirmed',
    label: 'Confirmed',
    backgroundColor: '#a62639',
    textColor: WHITE,
  },
  Requested: {
    key: 'Requested',
    label: 'Requested',
    backgroundColor: '#ff7373',
    textColor: BLACK,
  },
  Available: {
    key: 'Available',
    label: 'Available',
    backgroundColor: '#009b72',
    textColor: WHITE,
  },
  OnRequest: {
    key: 'OnRequest',
    label: 'On-Request',
    backgroundColor: '#fedc96',
    textColor: BLACK,
  },
}

function asResult(token: SessionLegendEntry): SessionColourResult {
  return {
    key: token.key,
    backgroundColor: token.backgroundColor,
    borderColor: token.backgroundColor,
    textColor: token.textColor,
  }
}

/**
 * First match wins, in this order: On-Hold, Unavailable, Reserved, Confirmed, Requested, Available,
 * On-Request. On-Hold stays on top the way it always has. Unavailable now sits above Reserved, so an
 * unavailable session is grey through and through rather than grey with a coloured border.
 */
function resolveSimplified(item: SessionColourContext): SessionColourResult {
  if (item.isOnHoldNow) {
    return asResult(V2_TOKENS.OnHold)
  }

  if (item.sessionStatus === STATUS.UNAVAILABLE) {
    return asResult(V2_TOKENS.Unavailable)
  }

  if (!item.booked && item.isReserved && item.canSeeReserved) {
    return asResult(V2_TOKENS.Reserved)
  }

  if (item.booked) {
    return asResult(item.confirmed ? V2_TOKENS.Confirmed : V2_TOKENS.Requested)
  }

  return asResult(
    item.sessionStatus === STATUS.AVAILABLE
      ? V2_TOKENS.Available
      : V2_TOKENS.OnRequest
  )
}

export const SIMPLIFIED_V2_SCHEME: SessionColourScheme = {
  id: SESSION_COLOUR_SCHEME.SIMPLIFIED_V2,
  resolve: resolveSimplified,
  /**
   * The six states the request names. On-Hold is left out on purpose: it is not in today's key
   * either, and the request enumerates six.
   */
  legend: [
    {
      entries: [
        V2_TOKENS.Available,
        V2_TOKENS.Reserved,
        V2_TOKENS.OnRequest,
        V2_TOKENS.Requested,
        V2_TOKENS.Confirmed,
        V2_TOKENS.Unavailable,
      ],
    },
  ],
  /** A case manager is never sent an unavailable session, so the grey swatch only puzzles them. */
  hiddenLegendKeysForCaseManager: ['Unavailable'],
  /** Same reason as the swatch: a case manager is never sent an unavailable session, so offering to
   * filter for them only invites a search that can return nothing. */
  hiddenFilterIdsForCaseManager: [BOOKING_STATUS.UNAVAILABLE],
  filterTitle: 'Availability Status',
  filterOptions: [
    { id: BOOKING_STATUS.UNAVAILABLE, displayName: 'Unavailable' },
    {
      id: BOOKING_STATUS.UNBOOKED_CONFIRMED_AVAILABLE,
      displayName: 'Available',
    },
    {
      id: BOOKING_STATUS.UNBOOKED_UNCONFIRMED_AVAILABLE,
      displayName: 'Available (On-Request)',
    },
    { id: BOOKING_STATUS.BOOKED_CONFIRMED, displayName: 'Booked (Confirmed)' },
    {
      id: BOOKING_STATUS.BOOKED_UNCONFIRMED,
      displayName: 'Booked (Requested)',
    },
  ],
  titleParts: ['specialist', 'client'],
  statusLabel: item => {
    if (item.booked) {
      return {
        text: item.confirmed ? 'Confirmed' : 'Requested',
        withAcceptType: true,
      }
    }
    if (item.sessionStatus === STATUS.UNAVAILABLE) {
      return { text: 'Specialist Unavailable', withAcceptType: false }
    }
    return {
      text:
        item.sessionStatus === STATUS.PENDING
          ? 'Available (On-Request)'
          : 'Available',
      withAcceptType: true,
    }
  },
}

const SCHEMES: Record<SessionColourSchemeId, SessionColourScheme> = {
  [SESSION_COLOUR_SCHEME.LEGACY_V1]: LEGACY_V1_SCHEME,
  [SESSION_COLOUR_SCHEME.SIMPLIFIED_V2]: SIMPLIFIED_V2_SCHEME,
}

/**
 * The scheme for an id. Anything unrecognised — an older server that does not send the key yet, a
 * value we do not know — falls back to the legacy scheme, so the calendar never changes appearance by
 * accident.
 */
export function getSessionColourScheme(id?: string): SessionColourScheme {
  return SCHEMES[id as SessionColourSchemeId] ?? LEGACY_V1_SCHEME
}

/**
 * The availability filter values to offer this viewer. Kept a plain function so the rule can be
 * tested on its own; `appStore.sessionFilterOptions` is what the screens actually read.
 */
export function visibleFilterOptions(
  scheme: SessionColourScheme,
  viewer: { isCaseManager?: boolean }
) {
  if (!viewer?.isCaseManager) {
    return scheme.filterOptions
  }

  return scheme.filterOptions.filter(
    option => !scheme.hiddenFilterIdsForCaseManager.includes(option.id)
  )
}
